import type { HTMLAttributes, ReactNode } from 'react'

import cx from 'classnames'
import { createContext, useContext, useRef, useState } from 'react'
import { useDebounce, useMeasure } from 'react-use'

import styles from './action-button-dropdown.module.css'

interface ActionButtonDropdownProps extends HTMLAttributes<HTMLDivElement> {
  buttonContent: ReactNode
}

const ONE_BUTTON_HEIGHT = 44
const AVERAGE_SIZE = ONE_BUTTON_HEIGHT * 3
const MARGIN = 33

export const ContainerMeasureContext = createContext<DOMRect | null>(null)

export function ContainerMeasureProvider ({ children }: { children: ReactNode }): JSX.Element {
  let [ tableContainerRef, measure ] = useMeasure<HTMLDivElement>()

  return (
    <div
      id='container-measure-provider'
      ref={tableContainerRef}
      className='h-full w-full flex-1'
    >
      <ContainerMeasureContext.Provider value={measure as DOMRect ?? null}>
        {children}
      </ContainerMeasureContext.Provider>
    </div>
  )
}

export function useContainerMeasureProvider (): DOMRect | null | undefined {
  return useContext(ContainerMeasureContext)
}

export function ActionButtonDropdown ({ className, buttonContent, children }: ActionButtonDropdownProps): JSX.Element {
  let [ isOpen, setIsOpen ] = useState(false)
  let [ debouncedIsOpen, setDebouncedIsOpen ] = useState(isOpen)
  let buttonsRef = useRef<HTMLDivElement>(null)
  let containerContext = useContainerMeasureProvider()

  let optionsPosition = (buttonsRef?.current?.offsetTop ?? 0) + AVERAGE_SIZE + MARGIN

  let positionTop = containerContext
    ? optionsPosition >= (containerContext?.height ?? 0)
    : false

  useDebounce(() => {
    setDebouncedIsOpen(isOpen)
  }, isOpen ? 50 : 150, [ isOpen ])

  return (
    <div
      ref={buttonsRef}
      className={cx(styles.wrapper, className)}
      onMouseEnter={() => {
        setIsOpen(true)
      }}
      onMouseLeave={() => {
        setIsOpen(false)
      }}
    >
      {children}

      {debouncedIsOpen && (
        <div className={cx(styles.content, className, [ positionTop && styles.positionTop ])}>
          {buttonContent}
        </div>
      )}
    </div>
  )
}
